import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './HowItWorks.css';

const HowItWorks = () => {
  const [activeTab, setActiveTab] = useState('customer');
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const customerSteps = [
    {
      number: 1,
      title: "Search Service",
      description: "Find what you need like plumber, electrician, or haircut.",
      icon: "🔍"
    },
    {
      number: 2,
      title: "Choose Tradie",
      description: "Compare profiles, ratings, and pricing.",
      icon: "👨‍🔧"
    },
    {
      number: 3,
      title: "Book Instantly",
      description: "Select your time and confirm booking.",
      icon: "📅"
    },
    {
      number: 4,
      title: "Job Done",
      description: "Relax while the expert completes your work.",
      icon: "✅"
    }
  ];

  const technicianSteps = [
    {
      number: 1,
      title: "Create Profile",
      description: "Add your skills and experience.",
      icon: "📝"
    },
    {
      number: 2,
      title: "Get Job Requests",
      description: "Receive nearby job notifications.",
      icon: "📱"
    },
    {
      number: 3,
      title: "Accept & Work",
      description: "Choose jobs and complete them.",
      icon: "🔧"
    },
    {
      number: 4,
      title: "Earn Money",
      description: "Get paid and build your reputation.",
      icon: "💰"
    }
  ];

  const trustFeatures = [
    { icon: "🛡️", label: "Verified Professionals" },
    { icon: "🔒", label: "Secure Payments" },
    { icon: "⚡", label: "Fast Response" },
    { icon: "⭐", label: "Real Reviews" }
  ];

  return (
    <section className="hiw-wrapper" ref={sectionRef}>
      {/* Background Decoration */}
      <div className="hiw-bg-decoration">
        <div className="hiw-bg-circle hiw-bg-circle-1"></div>
        <div className="hiw-bg-circle hiw-bg-circle-2"></div>
        <div className="hiw-bg-circle hiw-bg-circle-3"></div>
        <div className="hiw-bg-grid"></div>
      </div>

      <div className="hiw-container">
        {/* Hero Section */}
        <div className={`hiw-hero ${isVisible ? 'hiw-hero-visible' : ''}`}>
          <div className="hiw-hero-badge">
            <span className="hiw-hero-badge-dot"></span>
            How It Works
          </div>
          <h1 className="hiw-hero-title">
            Get Any Job Done in 
            <span className="hiw-gradient-text"> One Click</span>
          </h1>
          <p className="hiw-hero-subtitle">
            Simple. Fast. Reliable. Connect with trusted professionals instantly.
          </p>
        </div>

        {/* Toggle Switch */}
        <div className={`hiw-toggle-wrapper ${isVisible ? 'hiw-toggle-visible' : ''}`}>
          <div className="hiw-toggle">
            <button 
              className={`hiw-toggle-btn ${activeTab === 'customer' ? 'hiw-toggle-active' : ''}`}
              onClick={() => setActiveTab('customer')}
            >
              <span className="hiw-toggle-icon">👤</span>
              For Customers
            </button>
            <button 
              className={`hiw-toggle-btn ${activeTab === 'technician' ? 'hiw-toggle-active' : ''}`}
              onClick={() => setActiveTab('technician')}
            >
              <span className="hiw-toggle-icon">🔧</span>
              For Technicians
            </button>
            <div className={`hiw-toggle-slider ${activeTab === 'technician' ? 'hiw-toggle-slider-right' : ''}`}></div>
          </div>
        </div>

        {/* Steps Section */}
        <div className="hiw-steps-wrapper">
          <div className={`hiw-steps-grid ${activeTab === 'customer' ? 'hiw-steps-active' : 'hiw-steps-hidden'}`}>
            {customerSteps.map((step, index) => (
              <div 
                key={index} 
                className={`hiw-step-card ${isVisible ? 'hiw-step-visible' : ''}`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="hiw-step-number-wrapper">
                  <span className="hiw-step-number">{step.number}</span>
                  <span className="hiw-step-icon">{step.icon}</span>
                </div>
                <h3 className="hiw-step-title">{step.title}</h3>
                <p className="hiw-step-description">{step.description}</p>
                {index < customerSteps.length - 1 && (
                  <div className="hiw-step-connector">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className={`hiw-steps-grid ${activeTab === 'technician' ? 'hiw-steps-active' : 'hiw-steps-hidden'}`}>
            {technicianSteps.map((step, index) => (
              <div 
                key={index} 
                className={`hiw-step-card ${isVisible ? 'hiw-step-visible' : ''}`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                <div className="hiw-step-number-wrapper">
                  <span className="hiw-step-number">{step.number}</span>
                  <span className="hiw-step-icon">{step.icon}</span>
                </div>
                <h3 className="hiw-step-title">{step.title}</h3>
                <p className="hiw-step-description">{step.description}</p>
                {index < technicianSteps.length - 1 && (
                  <div className="hiw-step-connector">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Trust Section */}
        <div className={`hiw-trust-section ${isVisible ? 'hiw-trust-visible' : ''}`}>
          <div className="hiw-trust-header">
            <span className="hiw-trust-badge">Why Trust Us</span>
            <h2 className="hiw-trust-title">Built on Trust & Excellence</h2>
          </div>
          <div className="hiw-trust-grid">
            {trustFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="hiw-trust-item"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="hiw-trust-icon">{feature.icon}</div>
                <span className="hiw-trust-label">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className={`hiw-cta-section ${isVisible ? 'hiw-cta-visible' : ''}`}>
          <div className="hiw-cta-container">
            <h2 className="hiw-cta-title">Ready to Get Started?</h2>
            <p className="hiw-cta-subtitle">Join thousands of satisfied customers and technicians today</p>
            <div className="hiw-cta-buttons">
              <button className="hiw-btn-primary" onClick={()=>navigate('/services')}>
                <span className="hiw-btn-text" >Book a Service</span>
                <span className="hiw-btn-icon">→</span>
              </button>
              <button className="hiw-btn-secondary" onClick={()=>navigate('/login')}>
                <span className="hiw-btn-text">Become a Technician</span>
                <span className="hiw-btn-icon">→</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;